import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { labResultService } from '../services/labResultService'
import { LabResult, LabResultStatus, LabResultPanel } from '../types/labResult'
import { formatDate } from '../utils/locale'
import { MaskedField } from './MaskedField'
import './LabResultList.css'

interface LabResultListProps {
  patientId: string
  onLabResultClick?: (labResult: LabResult) => void
  navigateToDetail?: boolean // If true, navigate to detail page instead of calling onLabResultClick
}

export function LabResultList({ patientId, onLabResultClick, navigateToDetail = false }: LabResultListProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: labResults, isLoading, error } = useQuery({
    queryKey: ['lab-results', 'patient', patientId],
    queryFn: () => labResultService.getByPatient(patientId),
  })

  const reviewMutation = useMutation({
    mutationFn: (id: string) => labResultService.markAsReviewed(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-results', 'patient', patientId] })
    },
  })

  const handleMarkAsReviewed = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    try {
      await reviewMutation.mutateAsync(id)
    } catch (error) {
      alert('Failed to mark result as reviewed')
    }
  }

  const getStatusBadge = (status: LabResultStatus) => {
    const statusClasses = {
      pending: 'status-pending',
      final: 'status-final',
      preliminary: 'status-preliminary',
      amended: 'status-amended',
      cancelled: 'status-cancelled',
    }
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>
  }

  if (isLoading) {
    return <div className="loading">Loading lab results...</div>
  }

  if (error) {
    return <div className="error">Error loading lab results</div>
  }

  if (!labResults || labResults.length === 0) {
    return (
      <div className="lab-result-list-empty">
        <p>No lab results found for this patient.</p>
      </div>
    )
  }

  return (
    <MaskedField
      field="lab-results"
      value={labResults}
      patientId={patientId}
      confidentialityLevel="restricted"
    >
      {(canAccess, maskedLabResults, requestEmergencyAccess) => {
        if (!canAccess) {
          return (
            <div className="lab-result-list-empty">
              <p>You do not have permission to view lab results.</p>
              {requestEmergencyAccess && (
                <button
                  onClick={requestEmergencyAccess}
                  className="btn-secondary btn-small"
                  style={{ marginTop: '1rem' }}
                >
                  Request Emergency Access
                </button>
              )}
            </div>
          )
        }

        return (
          <div className="lab-result-list">
      <div className="lab-result-list-header">
        <h3>Lab Results ({maskedLabResults.length})</h3>
      </div>
      <div className="lab-result-items">
        {maskedLabResults.map((labResult: LabResult) => (
          <div
            key={labResult.id}
            className={`lab-result-item ${labResult.isCritical ? 'critical' : labResult.isAbnormal ? 'abnormal' : ''}`}
            onClick={() => {
              if (navigateToDetail) {
                navigate(`/lab-results/${labResult.id}`)
              } else {
                onLabResultClick?.(labResult)
              }
            }}
          >
            <div className="lab-result-item-header">
              <div className="lab-result-date">
                <strong>{formatDate(labResult.reportedDate)}</strong>
                {labResult.reportedTime && (
                  <span className="lab-result-time"> {labResult.reportedTime}</span>
                )}
              </div>
              <div className="lab-result-header-right">
                {labResult.reviewStatus === 'in-review' && (
                  <span className="review-badge in-review">In Review</span>
                )}
                {labResult.reviewStatus === 'reviewed' && (
                  <span className="review-badge reviewed">Reviewed</span>
                )}
                {getStatusBadge(labResult.status)}
                {labResult.isCritical && <span className="flag-badge critical">Critical</span>}
                {labResult.isAbnormal && !labResult.isCritical && (
                  <span className="flag-badge abnormal">Abnormal</span>
                )}
              </div>
            </div>
            <div className="lab-result-item-body">
              <div className="lab-result-lab">
                <strong>Lab:</strong> {labResult.labName}
              </div>
              {labResult.orderNumber && (
                <div className="lab-result-order">
                  <strong>Order:</strong> {labResult.orderNumber}
                </div>
              )}
              {labResult.collectionDate && (
                <div className="lab-result-collection">
                  <strong>Collected:</strong> {formatDate(labResult.collectionDate)}
                  {labResult.collectionTime && ` at ${labResult.collectionTime}`}
                </div>
              )}
              {labResult.clinicalIndication && (
                <div className="lab-result-indication">
                  <strong>Indication:</strong> {labResult.clinicalIndication}
                </div>
              )}
              <div className="lab-result-panels">
                <strong>Panels:</strong> {labResult.panels.length}{' '}
                {labResult.panels.length === 1 ? 'panel' : 'panels'} (
                {labResult.panels.reduce((sum: number, panel: LabResultPanel) => sum + panel.tests.length, 0)} tests)
              </div>
              {labResult.comments && (
                <div className="lab-result-comments">
                  {labResult.comments.length > 150
                    ? `${labResult.comments.substring(0, 150)}...`
                    : labResult.comments}
                </div>
              )}
              {labResult.reviewStatus === 'reviewed' && labResult.reviewedByName && (
                <div className="lab-result-reviewed">
                  <strong>Reviewed by:</strong> {labResult.reviewedByName}
                  {labResult.reviewedAt && (
                    <> on {formatDate(labResult.reviewedAt)}</>
                  )}
                </div>
              )}
            </div>
            {labResult.reviewStatus === 'in-review' && (
              <div className="lab-result-actions">
                <button
                  className="btn-review"
                  onClick={(e) => handleMarkAsReviewed(e, labResult.id)}
                  disabled={reviewMutation.isPending}
                >
                  {reviewMutation.isPending ? 'Marking...' : 'Mark as Reviewed'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
        )
      }}
    </MaskedField>
  )
}

